import { useState, useEffect } from 'react';
import axios from 'axios';
import useUnsavedChanges from '../hooks/useUnsavedChanges';
import useIndustries from '../hooks/useIndustries';

const COMPANY_SIZES = ['1–10', '11–50', '51–200', '201–500', '500+'];
const HOW_FOUND = ['Referral', 'Cold Call', 'LinkedIn', 'Website', 'Email', 'WhatsApp', 'Other'];
const RELATIONSHIP_OWNERS = ['ST Tee', 'Steph'];

const SERVICES = ['Recruitment', 'Training', 'Both'];

const emptyForm = {
  company_name: '', ssm_number: '', industry: '', company_size: '',
  pic_name: '', pic_email: '', pic_phone: '',
  address: '', billing_address: '', working_hours: '',
  credit_terms: '', payment_terms: '', placement_terms: '', subsidiaries: [],
  account_status: 'active', relationship_owner: '', how_found_us: '',
  services: 'Recruitment', company_overview: '', notes: '',
  interview_contact_name: '', interview_contact_department: '', interview_documents: []
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
}

function StatusBadge({ status }) {
  const styles = {
    active:    'bg-green-50 text-green-700 border-green-200',
    'on-hold': 'bg-yellow-50 text-yellow-700 border-yellow-200',
    inactive:  'bg-red-50 text-red-600 border-red-200'
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 border rounded-full ${styles[status] || styles.active}`}>
      {status === 'on-hold' ? 'On Hold' : status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

export default function Clients() {
  const [clients, setClients]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [search, setSearch]             = useState('');
  const [selected, setSelected]         = useState(null);
  const [showForm, setShowForm]         = useState(false);
  const [editMode, setEditMode]         = useState(false);
  const [form, setForm]                 = useState(emptyForm);
  const [submitting, setSubmitting]     = useState(false);
  const [formError, setFormError]       = useState('');
  const [locations, setLocations]       = useState([]);
  const [newLocation, setNewLocation]   = useState({ name: '', address: '' });
  const [addingLocation, setAddingLocation] = useState(false);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [activityLog, setActivityLog]   = useState([]);
  const [changeReason, setChangeReason] = useState('');
  const { markClean, confirmClose } = useUnsavedChanges();
  const { industries, addIndustry } = useIndustries();

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchClients(); }, []);

  async function fetchClients() {
    try {
      const res = await axios.get('https://carriera-backend.onrender.com/api/clients', { headers });
      setClients(res.data);
    } catch {
      setError('Failed to load clients.');
    } finally {
      setLoading(false);
    }
  }

  async function fetchLocations(clientId) {
    try {
      const res = await axios.get(`https://carriera-backend.onrender.com/api/client-locations/${clientId}`, { headers });
      setLocations(res.data);
    } catch {
      setLocations([]);
    }
  }

  async function fetchActivityLog(clientId) {
    try {
      const res = await axios.get(`https://carriera-backend.onrender.com/api/clients/${clientId}/activity`, { headers });
      setActivityLog(res.data);
    } catch {
      setActivityLog([]);
    }
  }

  async function handleAddLocation(e) {
    e.preventDefault();
    if (!newLocation.name || !newLocation.address) return;
    setAddingLocation(true);
    try {
      const res = await axios.post('https://carriera-backend.onrender.com/api/client-locations', {
        client_id: selected.id,
        name: newLocation.name,
        address: newLocation.address
      }, { headers });
      setLocations(prev => [...prev, res.data]);
      setNewLocation({ name: '', address: '' });
      setShowLocationForm(false);
    } catch {
      alert('Failed to add location.');
    } finally {
      setAddingLocation(false);
    }
  }

  async function handleDeleteLocation(locationId) {
    if (!window.confirm('Remove this location?')) return;
    try {
      await axios.delete(`https://carriera-backend.onrender.com/api/client-locations/${locationId}`, { headers });
      setLocations(prev => prev.filter(l => l.id !== locationId));
    } catch {
      alert('Failed to delete location.');
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError('');

    if (!form.company_name || !form.pic_name || !form.pic_email || !form.pic_phone) {
      setFormError('Company name, PIC name, email and phone are required.');
      return;
    }

    setSubmitting(true);
    try {
      if (editMode && selected) {
        const res = await axios.patch(`https://carriera-backend.onrender.com/api/clients/${selected.id}`, { ...form, change_reason: changeReason }, { headers });
        setClients(prev => prev.map(c => c.id === selected.id ? res.data : c));
        setSelected(res.data);
        fetchActivityLog(selected.id);
      } else {
        const res = await axios.post('https://carriera-backend.onrender.com/api/clients', form, { headers });
        setClients(prev => [res.data, ...prev]);
      }
      setShowForm(false);
      setEditMode(false);
      setForm(emptyForm);
      setChangeReason('');
    } catch {
      setFormError('Failed to save client. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  function openEdit(client) {
    const next = {
      company_name:       client.company_name || '',
      ssm_number:         client.ssm_number || '',
      industry:           client.industry || '',
      company_size:       client.company_size || '',
      pic_name:           client.pic_name || '',
      pic_email:          client.pic_email || '',
      pic_phone:          client.pic_phone || '',
      address:            client.address || '',
      billing_address:    client.billing_address || '',
      working_hours:      client.working_hours || '',
      credit_terms:       client.credit_terms || '',
      payment_terms:      client.payment_terms || '',
      placement_terms:    client.placement_terms || '',
      subsidiaries:       Array.isArray(client.subsidiaries) ? client.subsidiaries : [],
      account_status:     client.account_status || 'active',
      relationship_owner: client.relationship_owner || '',
      how_found_us:       client.how_found_us || '',
      services:           client.services || 'Recruitment',
      company_overview:   client.company_overview || '',
      notes:              client.notes || '',
      interview_contact_name:       client.interview_contact_name || '',
      interview_contact_department: client.interview_contact_department || '',
      interview_documents:          client.interview_documents || []
    };
    setForm(next);
    setChangeReason('');
    setEditMode(true);
    setShowForm(true);
    markClean(next);
  }

  const filtered = clients.filter(c =>
    !search ||
    c.company_name.toLowerCase().includes(search.toLowerCase()) ||
    (c.industry || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.pic_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const inputClass = "w-full border border-carriera-border px-3 py-2 text-sm text-carriera-text bg-white outline-none focus:border-carriera-accent transition-colors duration-200";
  const labelClass = "block text-xs text-carriera-muted uppercase tracking-widest mb-1";

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <p className="text-carriera-muted text-sm">Loading clients...</p>
    </div>
  );

  return (
    <div className="h-full flex flex-col">

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-carriera-muted uppercase tracking-widest mb-1">Business Development</p>
          <h2 className="font-serif text-3xl font-normal text-carriera-text">Clients</h2>
        </div>
        <button onClick={() => { setShowForm(true); setEditMode(false); setForm(emptyForm); setFormError(''); markClean(emptyForm); }}
          className="bg-carriera-text text-white text-sm font-medium px-4 py-2 hover:bg-carriera-accent transition-colors duration-200">
          + Add Client
        </button>
      </div>

      {error && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2">{error}</div>}

      {/* Search */}
      <div className="mb-6">
        <input type="text" placeholder="Search by company, industry or contact name..."
          value={search} onChange={e => setSearch(e.target.value)}
          className="w-full border border-carriera-border px-3 py-2 text-sm text-carriera-text bg-white outline-none focus:border-carriera-accent transition-colors duration-200" />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total Clients',  value: clients.length },
          { label: 'Active',         value: clients.filter(c => c.account_status === 'active').length },
          { label: 'On Hold / Inactive', value: clients.filter(c => c.account_status !== 'active').length },
        ].map(s => (
          <div key={s.label} className="bg-white border border-carriera-border p-4"
               style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <p className="text-xs text-carriera-muted uppercase tracking-widest mb-1">{s.label}</p>
            <p className="font-serif text-2xl font-normal text-carriera-text">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Client list */}
      <div className="bg-white border border-carriera-border flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-carriera-muted text-sm">
              {clients.length === 0 ? 'No clients yet. Add your first client.' : 'No clients match your search.'}
            </p>
          </div>
        ) : (
          filtered.map((client, i) => (
            <div key={client.id}
              onClick={() => { setSelected(client); fetchLocations(client.id); fetchActivityLog(client.id); setShowLocationForm(false); }}
              className={`flex items-center justify-between p-4 cursor-pointer hover:bg-carriera-bg transition-colors duration-200 ${i > 0 ? 'border-t border-carriera-border' : ''}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-carriera-text">{client.company_name}</p>
                <p className="text-xs text-carriera-muted mt-0.5">
                  {client.industry || '—'} &nbsp;·&nbsp; {client.pic_name} &nbsp;·&nbsp; {client.pic_phone}
                </p>
              </div>
              <div className="flex items-center gap-2 ml-4">
                {client.services && (
                  <span className="text-xs text-carriera-accent border border-carriera-accent px-2 py-0.5 rounded-full hidden sm:block">{client.services}</span>
                )}
                {client.relationship_owner && (
                  <span className="text-xs text-carriera-muted hidden sm:block">{client.relationship_owner}</span>
                )}
                <StatusBadge status={client.account_status} />
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── CLIENT DETAIL PANEL ── */}
      {selected && !showForm && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setSelected(null)}>
          <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>

            <div className="p-6 border-b border-carriera-border flex items-start justify-between">
              <div>
                <h3 className="font-serif text-xl font-normal text-carriera-text">{selected.company_name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <StatusBadge status={selected.account_status} />
                  {selected.ssm_number && <span className="text-xs text-carriera-muted">SSM: {selected.ssm_number}</span>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={async () => {
                  if (!window.confirm(`Delete ${selected.company_name}? This cannot be undone.`)) return;
                  try {
                    await axios.delete(`https://carriera-backend.onrender.com/api/clients/${selected.id}`, { headers });
                    setClients(prev => prev.filter(c => c.id !== selected.id));
                    setSelected(null);
                  } catch { alert('Failed to delete client.'); }
                }} className="text-xs text-red-400 hover:text-red-600 transition-colors duration-200">
                  Delete
                </button>
                <button onClick={() => setSelected(null)} className="text-carriera-muted hover:text-carriera-text text-lg">✕</button>
              </div>
            </div>

            <div className="p-6 space-y-5">

              {/* Contact */}
              <div>
                <p className="text-xs text-carriera-muted uppercase tracking-widest mb-2">Person In Charge</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Name',     value: selected.pic_name },
                    { label: 'Email',    value: selected.pic_email },
                    { label: 'Phone',    value: selected.pic_phone },
                    { label: 'Industry', value: selected.industry || '—' },
                    { label: 'Size',     value: selected.company_size || '—' },
                    { label: 'Working Hours', value: selected.working_hours || '—' },
                  ].map(item => (
                    <div key={item.label}>
                      <p className="text-xs text-carriera-muted uppercase tracking-widest mb-0.5">{item.label}</p>
                      <p className="text-sm text-carriera-text">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              <hr className="border-carriera-border" />

              {/* Commercial */}
              <div>
                <p className="text-xs text-carriera-muted uppercase tracking-widest mb-2">Commercial Terms</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Credit Terms',  value: selected.credit_terms || '—' },
                    { label: 'Payment Terms', value: selected.payment_terms || '—' },
                    { label: 'Placement Terms', value: selected.placement_terms || '—' },
                    { label: 'Relationship Owner', value: selected.relationship_owner || '—' },
                    { label: 'How Found Us',  value: selected.how_found_us || '—' },
                  ].map(item => (
                    <div key={item.label}>
                      <p className="text-xs text-carriera-muted uppercase tracking-widest mb-0.5">{item.label}</p>
                      <p className="text-sm text-carriera-text">{item.value}</p>
                    </div>
                  ))}
                </div>
                {Array.isArray(selected.subsidiaries) && selected.subsidiaries.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-carriera-muted uppercase tracking-widest mb-1">Subsidiaries</p>
                    <ul className="text-sm text-carriera-text space-y-0.5">
                      {selected.subsidiaries.map((s, i) => (
                        <li key={i}>{s.name}{s.ssm_number ? ` · ${s.ssm_number}` : ''}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <hr className="border-carriera-border" />

              {/* Address */}
              {(selected.address || selected.billing_address) && (
                <div>
                  <p className="text-xs text-carriera-muted uppercase tracking-widest mb-2">Address</p>
                  {selected.address && (
                    <div className="mb-3">
                      <p className="text-xs text-carriera-muted mb-0.5">Office</p>
                      <p className="text-sm text-carriera-text">{selected.address}</p>
                    </div>
                  )}
                  {selected.billing_address && (
                    <div>
                      <p className="text-xs text-carriera-muted mb-0.5">Billing</p>
                      <p className="text-sm text-carriera-text">{selected.billing_address}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Company Overview */}
              {selected.company_overview && (
                <div>
                  <p className="text-xs text-carriera-muted uppercase tracking-widest mb-1">Company Overview (for job postings)</p>
                  <p className="text-sm text-carriera-text leading-relaxed">{selected.company_overview}</p>
                </div>
              )}

              {/* Notes */}
              {selected.notes && (
                <div>
                  <p className="text-xs text-carriera-muted uppercase tracking-widest mb-1">Notes</p>
                  <p className="text-sm text-carriera-text leading-relaxed">{selected.notes}</p>
                </div>
              )}

              <hr className="border-carriera-border" />

              {/* Locations */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-carriera-muted uppercase tracking-widest">Interview Locations</p>
                  <button onClick={() => setShowLocationForm(!showLocationForm)}
                    className="text-xs bg-carriera-accent text-white px-3 py-1.5 hover:opacity-90 transition-opacity duration-200">
                    {showLocationForm ? 'Close' : '+ Add Location'}
                  </button>
                </div>

                {/* Add location form */}
                {showLocationForm && (
                  <form onSubmit={handleAddLocation} className="bg-carriera-bg border border-carriera-border p-4 space-y-3 mb-3">
                    <div>
                      <label className="block text-xs text-carriera-muted uppercase tracking-widest mb-1">Location Name *</label>
                      <input type="text" value={newLocation.name}
                        onChange={e => setNewLocation(p => ({ ...p, name: e.target.value }))}
                        placeholder="e.g. Head Office, Klang Warehouse, Penang Factory"
                        className="w-full border border-carriera-border px-3 py-2 text-sm text-carriera-text bg-white outline-none focus:border-carriera-accent transition-colors duration-200" />
                    </div>
                    <div>
                      <label className="block text-xs text-carriera-muted uppercase tracking-widest mb-1">Full Address *</label>
                      <textarea value={newLocation.address}
                        onChange={e => setNewLocation(p => ({ ...p, address: e.target.value }))}
                        rows={2} placeholder="Full address including postcode and state"
                        className="w-full border border-carriera-border px-3 py-2 text-sm text-carriera-text bg-white outline-none focus:border-carriera-accent transition-colors duration-200 resize-none" />
                    </div>
                    <button type="submit" disabled={addingLocation}
                      className="w-full bg-carriera-accent text-white text-xs font-medium py-2 hover:opacity-90 transition-opacity duration-200 disabled:opacity-50">
                      {addingLocation ? 'Adding...' : 'Save Location'}
                    </button>
                  </form>
                )}

                {/* Locations list */}
                {locations.length === 0 ? (
                  <p className="text-xs text-carriera-muted italic">No locations added yet.</p>
                ) : (
                  <div className="space-y-2">
                    {locations.map(loc => (
                      <div key={loc.id} className="flex items-start justify-between bg-carriera-bg border border-carriera-border p-3">
                        <div className="flex-1 min-w-0 mr-3">
                          <p className="text-sm font-medium text-carriera-text">{loc.name}</p>
                          <p className="text-xs text-carriera-muted mt-0.5 leading-relaxed">{loc.address}</p>
                        </div>
                        <button onClick={() => handleDeleteLocation(loc.id)}
                          className="text-xs text-red-400 hover:text-red-600 transition-colors duration-200 flex-shrink-0">
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <hr className="border-carriera-border" />

              {/* Activity Log */}
              <div>
                <p className="text-xs text-carriera-muted uppercase tracking-widest mb-2">Activity Log</p>
                {activityLog.length === 0 ? (
                  <p className="text-xs text-carriera-muted italic">No activity recorded yet.</p>
                ) : (
                  <div className="space-y-0">
                    {activityLog.map((entry, idx) => (
                      <div key={entry.id} className="flex gap-3">
                        <div className="flex flex-col items-center pt-1">
                          <div className="w-2 h-2 rounded-full bg-carriera-accent flex-shrink-0" />
                          {idx < activityLog.length - 1 && (
                            <div className="flex-1 w-px bg-carriera-border mt-1" style={{ minHeight: 20 }} />
                          )}
                        </div>
                        <div className="pb-4 flex-1">
                          <p className="text-xs text-carriera-text leading-relaxed">{entry.details}</p>
                          <p className="text-xs text-carriera-muted mt-0.5">{formatDate(entry.created_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <hr className="border-carriera-border" />

              {/* Edit button */}
              <button onClick={() => openEdit(selected)}
                className="w-full bg-carriera-text text-white text-sm font-medium py-2.5 hover:bg-carriera-accent transition-colors duration-200">
                Edit Client
              </button>

            </div>
          </div>
        </div>
      )}

      {/* ── ADD / EDIT CLIENT FORM ── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => confirmClose(form, () => { setShowForm(false); setEditMode(false); })}>
          <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>

            <div className="p-6 border-b border-carriera-border flex items-start justify-between">
              <div>
                <h3 className="font-serif text-xl font-normal text-carriera-text">
                  {editMode ? 'Edit Client' : 'Add New Client'}
                </h3>
                <p className="text-sm text-carriera-muted mt-0.5">
                  {editMode ? 'Update client information' : 'Onboard a new client company'}
                </p>
              </div>
              <button onClick={() => confirmClose(form, () => { setShowForm(false); setEditMode(false); })}
                className="text-carriera-muted hover:text-carriera-text text-lg">✕</button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">

              {/* Company info */}
              <div>
                <p className="text-xs font-medium text-carriera-accent uppercase tracking-widest mb-3">Company Information</p>
                <div className="space-y-3">
                  <div>
                    <label className={labelClass}>Company Name <span className="text-red-500">*</span></label>
                    <input type="text" value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))}
                      placeholder="e.g. DNE Total Logistics Sdn Bhd" className={inputClass} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>SSM Number</label>
                      <input type="text" value={form.ssm_number} onChange={e => setForm(p => ({ ...p, ssm_number: e.target.value }))}
                        placeholder="e.g. 123456-X" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Company Size</label>
                      <select value={form.company_size} onChange={e => setForm(p => ({ ...p, company_size: e.target.value }))} className={inputClass}>
                        <option value="">Select…</option>
                        {COMPANY_SIZES.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Industry</label>
                    <input list="industries-list" value={form.industry}
                      onChange={e => setForm(p => ({ ...p, industry: e.target.value }))}
                      onBlur={e => addIndustry(e.target.value)}
                      placeholder="Type or select…" className={inputClass} />
                    <datalist id="industries-list">
                      {industries.map(i => <option key={i} value={i} />)}
                    </datalist>
                  </div>
                  <div>
                    <label className={labelClass}>Working Hours</label>
                    <input type="text" value={form.working_hours} onChange={e => setForm(p => ({ ...p, working_hours: e.target.value }))}
                      placeholder="e.g. Mon–Fri, 9am–6pm" className={inputClass} />
                  </div>
                </div>
              </div>

              <hr className="border-carriera-border" />

              {/* Person in charge */}
              <div>
                <p className="text-xs font-medium text-carriera-accent uppercase tracking-widest mb-3">Person In Charge</p>
                <div className="space-y-3">
                  <div>
                    <label className={labelClass}>Full Name <span className="text-red-500">*</span></label>
                    <input type="text" value={form.pic_name} onChange={e => setForm(p => ({ ...p, pic_name: e.target.value }))}
                      placeholder="e.g. Ms. Kayathiri" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Email <span className="text-red-500">*</span></label>
                    <input type="email" value={form.pic_email} onChange={e => setForm(p => ({ ...p, pic_email: e.target.value }))}
                      placeholder="contact@company.com" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Phone <span className="text-red-500">*</span></label>
                    <input type="text" value={form.pic_phone} onChange={e => setForm(p => ({ ...p, pic_phone: e.target.value }))}
                      placeholder="+60 12-345 6789" className={inputClass} />
                  </div>
                </div>
              </div>

              <hr className="border-carriera-border" />

              {/* Address */}
              <div>
                <p className="text-xs font-medium text-carriera-accent uppercase tracking-widest mb-3">Address</p>
                <div className="space-y-3">
                  <div>
                    <label className={labelClass}>Office Address</label>
                    <textarea value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                      rows={2} placeholder="Full office address" className={inputClass} style={{ resize: 'vertical' }} />
                  </div>
                  <div>
                    <label className={labelClass}>Billing Address <span className="text-carriera-muted font-normal">(if different)</span></label>
                    <textarea value={form.billing_address} onChange={e => setForm(p => ({ ...p, billing_address: e.target.value }))}
                      rows={2} placeholder="Leave blank if same as office" className={inputClass} style={{ resize: 'vertical' }} />
                  </div>
                </div>
              </div>

              <hr className="border-carriera-border" />

              {/* Commercial */}
              <div>
                <p className="text-xs font-medium text-carriera-accent uppercase tracking-widest mb-3">Commercial Terms</p>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>Credit Terms</label>
                      <input type="text" value={form.credit_terms} onChange={e => setForm(p => ({ ...p, credit_terms: e.target.value }))}
                        placeholder="e.g. 30 days" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Payment Terms</label>
                      <input type="text" value={form.payment_terms} onChange={e => setForm(p => ({ ...p, payment_terms: e.target.value }))}
                        placeholder="e.g. Bank transfer" className={inputClass} />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Placement Terms <span className="text-carriera-muted font-normal">(shown at placement)</span></label>
                    <input type="text" value={form.placement_terms} onChange={e => setForm(p => ({ ...p, placement_terms: e.target.value }))}
                      placeholder="e.g. 90%, 30 Days; 12%, 90 Days" className={inputClass} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>Relationship Owner</label>
                      <select value={form.relationship_owner} onChange={e => setForm(p => ({ ...p, relationship_owner: e.target.value }))} className={inputClass}>
                        <option value="">Select…</option>
                        {RELATIONSHIP_OWNERS.map(r => <option key={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>How Found Us</label>
                      <select value={form.how_found_us} onChange={e => setForm(p => ({ ...p, how_found_us: e.target.value }))} className={inputClass}>
                        <option value="">Select…</option>
                        {HOW_FOUND.map(h => <option key={h}>{h}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Account Status</label>
                    <select value={form.account_status} onChange={e => setForm(p => ({ ...p, account_status: e.target.value }))} className={inputClass}>
                      <option value="active">Active</option>
                      <option value="on-hold">On Hold</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Services</label>
                    <select value={form.services} onChange={e => setForm(p => ({ ...p, services: e.target.value }))} className={inputClass}>
                      {SERVICES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <hr className="border-carriera-border" />

              {/* Subsidiaries */}
              <div>
                <p className="text-xs font-medium text-carriera-accent uppercase tracking-widest mb-1">Subsidiaries</p>
                <p className="text-xs text-carriera-muted mb-3">Companies under this client that may be the actual employer at placement. Used on the JTK report and invoice.</p>
                <div className="space-y-2">
                  {(form.subsidiaries || []).map((sub, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <input type="text" value={sub.name || ''} placeholder="Subsidiary name"
                        onChange={e => setForm(p => { const s = [...p.subsidiaries]; s[i] = { ...s[i], name: e.target.value }; return { ...p, subsidiaries: s }; })}
                        className={`${inputClass} col-span-4`} />
                      <input type="text" value={sub.ssm_number || ''} placeholder="SSM no."
                        onChange={e => setForm(p => { const s = [...p.subsidiaries]; s[i] = { ...s[i], ssm_number: e.target.value }; return { ...p, subsidiaries: s }; })}
                        className={`${inputClass} col-span-3`} />
                      <input type="text" value={sub.address || ''} placeholder="Address"
                        onChange={e => setForm(p => { const s = [...p.subsidiaries]; s[i] = { ...s[i], address: e.target.value }; return { ...p, subsidiaries: s }; })}
                        className={`${inputClass} col-span-4`} />
                      <button type="button" title="Remove"
                        onClick={() => setForm(p => ({ ...p, subsidiaries: p.subsidiaries.filter((_, j) => j !== i) }))}
                        className="col-span-1 text-carriera-muted hover:text-red-500 text-sm">✕</button>
                    </div>
                  ))}
                </div>
                <button type="button"
                  onClick={() => setForm(p => ({ ...p, subsidiaries: [...(p.subsidiaries || []), { name: '', ssm_number: '', address: '' }] }))}
                  className="mt-2 text-xs text-carriera-accent hover:underline">+ Add subsidiary</button>
              </div>

              <hr className="border-carriera-border" />

              {/* Company Overview */}
              <div>
                <p className="text-xs font-medium text-carriera-accent uppercase tracking-widest mb-3">Job Posting</p>
                <div>
                  <label className={labelClass}>Company Overview</label>
                  <p className="text-xs text-carriera-muted mb-2">Describe the company without mentioning the name — used in job postings automatically.</p>
                  <textarea value={form.company_overview} onChange={e => setForm(p => ({ ...p, company_overview: e.target.value }))}
                    rows={5} placeholder="e.g. Our client is a well-established logistics company with over 20 years of experience, operating across Peninsular Malaysia with a fleet of 500+ vehicles..."
                    className={inputClass} style={{ resize: 'vertical' }} />
                </div>
              </div>

              <hr className="border-carriera-border" />

              {/* Interview Defaults */}
              <div>
                <p className="text-xs font-medium text-carriera-accent uppercase tracking-widest mb-3">Interview Defaults</p>
                <p className="text-xs text-carriera-muted mb-3">Pre-filled when scheduling an interview for this client.</p>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={labelClass}>Contact Person</label>
                    <input type="text" value={form.interview_contact_name}
                      onChange={e => setForm(p => ({ ...p, interview_contact_name: e.target.value }))}
                      placeholder="e.g. Ms. Sarah HR" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Department</label>
                    <input type="text" value={form.interview_contact_department}
                      onChange={e => setForm(p => ({ ...p, interview_contact_department: e.target.value }))}
                      placeholder="e.g. Human Resources" className={inputClass} />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Standard Documents to Bring</label>
                  <p className="text-xs text-carriera-muted mb-1">One document per line.</p>
                  <textarea rows={4}
                    value={(form.interview_documents || []).join('\n')}
                    onChange={e => setForm(p => ({ ...p, interview_documents: e.target.value.split('\n') }))}
                    placeholder={"Updated Resume\nRelevant Certificates\nPay Slips (if any)"}
                    className={inputClass} style={{ resize: 'vertical' }} />
                </div>
              </div>

              <hr className="border-carriera-border" />

              {/* Notes */}
              <div>
                <label className={labelClass}>Notes</label>
                <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  rows={3} placeholder="Any additional notes about this client..."
                  className={inputClass} style={{ resize: 'vertical' }} />
              </div>

              {editMode && (
                <div>
                  <label className={labelClass}>Reason for change <span className="text-carriera-muted font-normal">(optional)</span></label>
                  <textarea rows={2} value={changeReason} onChange={e => setChangeReason(e.target.value)}
                    placeholder="e.g. Updated working hours per client" className={inputClass} style={{ resize: 'none' }} />
                  <p className="text-xs text-carriera-muted mt-1">Saved to this client's activity log alongside what changed.</p>
                </div>
              )}

              {formError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2">{formError}</div>
              )}

              <button type="submit" disabled={submitting}
                className="w-full bg-carriera-text text-white text-sm font-medium py-2.5 hover:bg-carriera-accent transition-colors duration-200 disabled:opacity-50">
                {submitting ? 'Saving...' : editMode ? 'Save Changes' : 'Add Client'}
              </button>

            </form>
          </div>
        </div>
      )}

    </div>
  );
}
